import { logEvent, setUserId, setUserProperties } from 'firebase/analytics'
import { analytics } from './firebase'

/**
 * Firebase Analytics Events
 */

// Set user ID for analytics
export function setAnalyticsUserId(uid: string) {
  if (analytics) {
    setUserId(analytics, uid)
  }
}

// Set user properties
export function setAnalyticsUserProperties(properties: Record<string, string>) {
  if (analytics) {
    setUserProperties(analytics, properties)
  }
}

// ============ AUTH EVENTS ============

// User signed in anonymously
export function logAnonymousSignIn() {
  if (analytics) {
    logEvent(analytics, 'anonymous_sign_in', {
      timestamp: new Date().toISOString(),
    })
  }
}

// ============ LETTER EVENTS ============

// User started writing a letter
export function logLetterStarted() {
  if (analytics) {
    logEvent(analytics, 'letter_started', {
      timestamp: new Date().toISOString(),
    })
  }
}

// Letter created successfully
export function logLetterCreated(letterId: string, charCount: number) {
  if (analytics) {
    logEvent(analytics, 'letter_created', {
      letter_id: letterId,
      char_count: charCount,
      timestamp: new Date().toISOString(),
    })
  }
}

// Letter rejected by moderation
export function logLetterRejected(reason: string) {
  if (analytics) {
    logEvent(analytics, 'letter_rejected', {
      rejection_reason: reason,
      timestamp: new Date().toISOString(),
    })
  }
}

// Letter creation failed (error)
export function logLetterFailed(error: string) {
  if (analytics) {
    logEvent(analytics, 'letter_failed', {
      error_message: error,
      timestamp: new Date().toISOString(),
    })
  }
}

// User requested a random letter
export function logLetterRequested() {
  if (analytics) {
    logEvent(analytics, 'letter_requested', {
      timestamp: new Date().toISOString(),
    })
  }
}

// User received and read a letter
export function logLetterRead(letterId: string) {
  if (analytics) {
    logEvent(analytics, 'letter_read', {
      letter_id: letterId,
      timestamp: new Date().toISOString(),
    })
  }
}

// No more letters available
export function logNoMoreLetters() {
  if (analytics) {
    logEvent(analytics, 'no_more_letters', {
      timestamp: new Date().toISOString(),
    })
  }
}

// ============ SPECIAL LETTER EVENTS ============

// User requested special letter
export function logSpecialLetterRequested() {
  if (analytics) {
    logEvent(analytics, 'special_letter_requested', {
      timestamp: new Date().toISOString(),
    })
  }
}

// User received and read special letter
export function logSpecialLetterRead(letterId: string) {
  if (analytics) {
    logEvent(analytics, 'special_letter_read', {
      letter_id: letterId,
      timestamp: new Date().toISOString(),
    })
  }
}

// Special letter fetch failed
export function logSpecialLetterError(error: string) {
  if (analytics) {
    logEvent(analytics, 'special_letter_error', {
      error_message: error,
      timestamp: new Date().toISOString(),
    })
  }
}

// ============ NAVIGATION EVENTS ============

// Page view
export function logPageView(pageName: string) {
  if (analytics) {
    logEvent(analytics, 'page_view', {
      page_name: pageName,
      timestamp: new Date().toISOString(),
    })
  }
}

// Button click
export function logButtonClick(buttonName: string, pageName: string) {
  if (analytics) {
    logEvent(analytics, 'button_click', {
      button_name: buttonName,
      page_name: pageName,
      timestamp: new Date().toISOString(),
    })
  }
}

// ============ PEPAPP PREMIUM MODAL EVENTS ============

// PepApp Premium modal was shown (after letter sent)
export function logPepAppModalShown() {
  if (analytics) {
    logEvent(analytics, 'pepapp_modal_shown', {
      timestamp: new Date().toISOString(),
    })
  }
}

// User clicked a button in PepApp Premium modal (action: 'open_app' | 'dismiss' | 'close')
export function logPepAppModalButtonClick(action: 'open_app' | 'dismiss' | 'close') {
  if (analytics) {
    logEvent(analytics, 'pepapp_modal_button_click', {
      action,
      timestamp: new Date().toISOString(),
    })
  }
}

// ============ ENGAGEMENT EVENTS ============

// User shared the app (if implemented)
export function logShare(method: string) {
  if (analytics) {
    logEvent(analytics, 'share', {
      method: method,
      timestamp: new Date().toISOString(),
    })
  }
}

// Session duration (call on unmount/leave)
export function logSessionEnd(durationSeconds: number) {
  if (analytics) {
    logEvent(analytics, 'session_end', {
      duration_seconds: durationSeconds,
      timestamp: new Date().toISOString(),
    })
  }
}
